import React, { useState } from "react";

import {
  faLock,
  faLockOpen,
  faRecycle,
} from "@fortawesome/free-solid-svg-icons";
import {
  faMapMarker,
  faMicroscope,
  faPencil,
  faAddressCard,
  faArrowProgress,
  faCopy,
  faArrowsToCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { scheduleRetry } from "@redux-offline/redux-offline/lib/actions";
import { useDispatch, useSelector } from "react-redux";

import {
  bulkUpdateScanNLISStatus,
  fetchLivestockSaleDataChanges,
  openDispatchingModal,
} from "actions";

import { patchDeploymentSale } from "actions/offline/deploymentSale";

import { TemporaryBusinessesDialog } from "components/Business/TemporaryBusinesses";
import { MultiButton } from "components/Button";
import { CopyBidderDirection } from "components/CopyBidderRegistrations/lib";
import { Row } from "components/Layout";

import { ModalTypes } from "constants/navigation";
import {
  DeploymentPermission,
  LivestockSalePermission,
  SaleyardPermission,
} from "constants/permissions";

import {
  getLivestockSaleId,
  openBusinessesInSale,
  openCreateSellViaExAgentSaleModal,
  openEditCheckpointModal,
  openRuleTester,
  openSaleModal,
  openSaleProgressOverviewHashRoute,
} from "lib/navigation";
import toast from "lib/toast";

import {
  getActiveLivestockAgentDeployment,
  getBidders,
  getCurrentSale,
  getIsFetchingBidders,
  getIsLivestockAgent,
  getIsNominationsLocked,
  getIsOnline,
  getIsStaff,
  selectUsedTemporaryBusinessesInCurrentSale,
} from "selectors";

import {
  useCanCopyBidderRegistrations,
  useCanCreateSellViaExAgentSaleFromSale,
  useHasBillingPermission,
  useHasDeploymentPermission,
  useHasPermission,
  useHasSaleyardOrDeploymentPermission,
  useHasTemporaryBusinessesPermission,
} from "hooks";

export function SaleActionsButton({ isMobile }) {
  const dispatch = useDispatch();

  const isLivestockAgent = useSelector(getIsLivestockAgent);
  const isStaff = useSelector(getIsStaff);

  const sale = useSelector(getCurrentSale);

  const deploymentSale = sale.deployment_sales[0];
  const nominationsLocked = useSelector(getIsNominationsLocked);

  const deploymentId = useSelector(state =>
    isLivestockAgent ? getActiveLivestockAgentDeployment(state).id : null,
  );

  const hasUpdateSalePermission = useHasPermission(
    getCurrentSale,
    LivestockSalePermission.update,
  );

  const hasBillingPermission = useHasBillingPermission();

  const hasCreateCheckpointPermission = useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.canCreateCheckpoint,
    SaleyardPermission.canCreateCheckpoint,
  );

  const hasTemporaryBusinessesPermission =
    useHasTemporaryBusinessesPermission();
  const temporaryBusinessesCount = useSelector(
    state => selectUsedTemporaryBusinessesInCurrentSale(state).length,
  );

  const [isPromptButtonEnabled, setIsPromptButtonEnabled] = useState(true);
  const [isTemporaryBusinessesDialogOpen, setIsTemporaryBusinessesDialogOpen] =
    useState(false);
  const [isUpdateScansButtonEnabled, setIsUpdateScansButtonEnabled] =
    useState(true);

  const hasCanLockNominationsPermission = useHasDeploymentPermission(
    DeploymentPermission.canLockNominations,
  );

  const isOnline = useSelector(getIsOnline);

  const canCreateSellViaExternalAgentSaleFromSale =
    useCanCreateSellViaExAgentSaleFromSale(sale.livestocksale_id);

  const canCopyBidderRegistrations = useCanCopyBidderRegistrations();
  const hasBidders = useSelector(
    state => Object.keys(getBidders(state)).length > 0,
  );
  const isFetchingBidders = useSelector(getIsFetchingBidders);

  const openCopyBidderRegistrations = direction => {
    dispatch(
      openDispatchingModal(
        ModalTypes.CopyBidderRegistrations,
        window.location.hash,
        { direction },
      ),
    );
  };

  const handlePromptSync = () => {
    toast.success("Prompting changes since synchronization");
    dispatch(fetchLivestockSaleDataChanges(sale, sale.saleyard_name));
    setIsPromptButtonEnabled(false);
    setTimeout(() => {
      setIsPromptButtonEnabled(true);
    }, 10000);
  };

  const handleUpdateScanNLISStatus = () => {
    dispatch(bulkUpdateScanNLISStatus());
    setIsUpdateScansButtonEnabled(false);
    setTimeout(() => {
      setIsUpdateScansButtonEnabled(true);
    }, 10000);
  };

  const handleRunQueue = () => {
    dispatch(scheduleRetry());
  };

  const handleOpenRuleTester = () => {
    openRuleTester("", [getLivestockSaleId()]);
  };

  const handleLockNominations = () => {
    dispatch(
      patchDeploymentSale(
        { is_nominations_locked: !nominationsLocked },
        deploymentSale.deployment_sale_id,
        getLivestockSaleId(),
      ),
    );
  };

  const buttons = [
    {
      title: "Edit Sale",
      icon: faPencil,
      dataTour: "editSale",
      default: true,
      isDisabled: !hasUpdateSalePermission,
      onClick: () => openSaleModal(getLivestockSaleId()),
    },

    {
      title: `Business In Sale`,
      icon: faAddressCard,
      onClick: openBusinessesInSale,
    },

    canCopyBidderRegistrations && {
      title: "Copy Bidders From Another Sale",
      dataTour: "copy-bidders-from-another-sale",
      icon: faCopy,
      onClick: () => openCopyBidderRegistrations(CopyBidderDirection.FROM),
      isDisabled: !isOnline || isFetchingBidders || hasBidders,
      tooltip: isFetchingBidders
        ? "Loading bidders..."
        : hasBidders
          ? "This sale already has bidders registered."
          : undefined,
    },

    canCopyBidderRegistrations && {
      title: "Copy Bidders To Another Sale",
      dataTour: "copy-bidders-to-another-sale",
      icon: faCopy,
      onClick: () => openCopyBidderRegistrations(CopyBidderDirection.TO),
      isDisabled: !isOnline || isFetchingBidders || !hasBidders,
      tooltip: isFetchingBidders
        ? "Loading bidders..."
        : hasBidders
          ? undefined
          : "This sale has no bidders to copy.",
    },

    hasTemporaryBusinessesPermission && {
      title: `Temporary Businesses (${temporaryBusinessesCount})`,
      icon: faArrowsToCircle,
      onClick: () => setIsTemporaryBusinessesDialogOpen(true),
      isDisabled: temporaryBusinessesCount === 0,
    },

    hasCreateCheckpointPermission && {
      title: "Add Checkpoint",
      icon: faMapMarker,
      onClick: () => openEditCheckpointModal(null, { deploymentId }),
    },

    isStaff && {
      title: "Run Changes Since",
      icon: faRecycle,
      onClick: handlePromptSync,
      isDisabled: !isPromptButtonEnabled,
    },

    isStaff && {
      title: "Trigger Retry Queue",
      icon: faRecycle,
      onClick: handleRunQueue,
    },

    isStaff && {
      title: "Refresh Scans",
      icon: faRecycle,
      onClick: handleUpdateScanNLISStatus,
      isDisabled: !isUpdateScansButtonEnabled,
    },

    hasBillingPermission && {
      title: "Test RuleBook",
      icon: faMicroscope,
      onClick: handleOpenRuleTester,
    },

    {
      title: "Sale Progress Overview",
      icon: faArrowProgress,
      onClick: openSaleProgressOverviewHashRoute,
    },

    hasCanLockNominationsPermission && {
      title: `${nominationsLocked ? "Unlock" : "Lock"} Nominations`,
      icon: nominationsLocked ? faLock : faLockOpen,
      onClick: handleLockNominations,
    },

    canCreateSellViaExternalAgentSaleFromSale && {
      title: "Create Sell via Ex Agent Sale",
      icon: faCopy,
      onClick: () => openCreateSellViaExAgentSaleModal(getLivestockSaleId()),
      isDisabled: !isOnline,
    },
  ].filter(Boolean);

  return (
    <Row alignStart paddingVertical={isMobile ? 0 : 2}>
      <MultiButton buttons={buttons} />
      {isTemporaryBusinessesDialogOpen && (
        <TemporaryBusinessesDialog
          livestockSaleId={sale.livestocksale_id}
          onClose={() => setIsTemporaryBusinessesDialogOpen(false)}
        />
      )}
    </Row>
  );
}
